import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { appendToRmaApiUrl } from '@rma/generic/ng/util';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { ShortlistResult } from '../domain/shortlist';
import { ShortlistStorageService } from './shortlist-storage.service';

@Injectable({
  providedIn: 'root',
})
export class ApiShortlistStorageService implements ShortlistStorageService {
  private readonly httpClient = inject(HttpClient);
  private readonly rmaApiUrl = inject(appendToRmaApiUrl);

  private readonly shorlistAgentsByCode = 'users/me/shortlist/agents/code-{code}';

  public load(): Observable<string[]> {
    return this.httpClient.get<string[]>(this.rmaApiUrl('users/me/shortlist/agents/codes'));
  }

  public add(agentCode: string): Observable<ShortlistResult> {
    return this.httpClient.put(this.rmaApiUrl(this.shorlistAgentsByCode.replace('{code}', agentCode)), null).pipe(
      map(() => ({ agentCode, added: true, success: true })),
      catchError((e) => {
        console.error(e);
        return of({ agentCode, added: true, success: false, error: e });
      }),
    );
  }

  public addMany(agentCodes: string[]): Observable<ShortlistResult[]> {
    return this.httpClient.put<string[]>(this.rmaApiUrl('users/me/shortlist/agents'), agentCodes).pipe(
      map((codes: string[]) => codes.map((agentCode) => ({ agentCode, added: true, success: true }))),
      catchError((e) => {
        console.error(e);
        return of(
          agentCodes.map((agentCode) => ({
            agentCode,
            added: true,
            success: false,
            error: e,
          })),
        );
      }),
    );
  }

  // There is no bulk-delete endpoint, so a clear is one delete per code the server currently holds,
  // issued in parallel. The codes come from `load` because the server, not the caller, is the
  // authority on what is still shortlisted; a load failure propagates so nothing is cleared blind.
  public clear(): Observable<ShortlistResult[]> {
    return this.load().pipe(
      switchMap((agentCodes) =>
        agentCodes.length ? forkJoin(agentCodes.map((agentCode) => this.deleteCode(agentCode))) : of<ShortlistResult[]>([]),
      ),
    );
  }

  public remove(agentCode: string): Observable<ShortlistResult> {
    return this.httpClient.delete(this.rmaApiUrl(this.shorlistAgentsByCode.replace('{code}', agentCode))).pipe(
      catchError((e) => {
        console.error(e);
        return of({ agentCode, added: false, success: false, error: e });
      }),
      map(() => ({ agentCode, added: false, success: true })),
    );
  }

  // `remove` recovers an error and then maps it back to `success: true`, so its caller cannot tell a
  // failed delete from a successful one. A clear must make that distinction — the failures are what
  // stay in local state — so it deletes through here rather than reusing `remove`.
  private deleteCode(agentCode: string): Observable<ShortlistResult> {
    return this.httpClient.delete(this.rmaApiUrl(this.shorlistAgentsByCode.replace('{code}', agentCode))).pipe(
      map(() => ({ agentCode, added: false, success: true })),
      catchError((e) => {
        console.error(e);
        return of({ agentCode, added: false, success: false, error: e });
      }),
    );
  }
}
